import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  useActionDispositivo,
} from "@/api";
import { SmallLoading } from "@/components/Loading";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { DispositivoAction } from "../..";

export type GFL_DFA_3500_Action =
  | "start"
  | "start-manuale"
  | "stop"
  | "stop-manuale"
  | "reset"
  | "start diso-man"
  | "start filt-man"
  | "stop diso-man"
  | "stop filt-man";

export function GFL_DFA_3500_Buttons({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const [sentAction, setSentAction] = useState<DispositivoAction | null>(null);

  const { mutateAsync: setActionAsync, isLoading: actionLoading } =
    useActionDispositivo();

  const queryClient = useQueryClient();

  async function setAction(action: DispositivoAction) {
    try {
      setSentAction(action);
      const response = await setActionAsync({
        id: nodo.id,
        data: { action: action },
      });
      if (response.data?.updated) {
        toast.success(t("dispositivoActions.toast.success"));
      } else {
        toast.warn(t("dispositivoActions.toast.noResponse"));
      }
    } catch (e) {
      toast.error(t("dispositivoActions.toast.error"));
    } finally {
      setSentAction(null);
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
    }
  }

  const status = nodo.dispositivoLastStatus;

  const canStart = status === "stop" || status === "stop-manuale";
  const canStop = status === "on";
  const canReset = status === "alarm";
  const canStartDisoMan =
    status === "stop" ||
    status === "stop-manuale" ||
    status === "on-manuale filt";
  const canStopDisoMan =
    status === "on-manuale diso" || status === "on pompe manuale";
  const canStartFiltMan =
    status === "stop" ||
    status === "stop-manuale" ||
    status === "on-manuale diso";
  const canStopFiltMan =
    status === "on-manuale filt" || status === "on pompe manuale";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: "340px",
      }}
    >
      {/* Controlli modalità automatica */}
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStart}
            onClick={() => setAction("start")}
            startIcon={
              sentAction === "start" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflDfa3500.buttons.start")}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStop}
            onClick={() => setAction("stop")}
            startIcon={sentAction === "stop" && <SmallLoading sx={{ mr: 1 }} />}
            fullWidth
          >
            {t("gflDfa3500.buttons.stop")}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canReset}
            onClick={() => setAction("reset")}
            startIcon={
              sentAction === "reset" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("azioni.reset")}
          </Button>
        </Grid>
      </Grid>

      {/* Controlli modalità manuale */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStartFiltMan}
            onClick={() => setAction("start filt-man")}
            startIcon={
              sentAction === "start filt-man" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflDfa3500.buttons.startFiltrazione")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStopFiltMan}
            onClick={() => setAction("stop filt-man")}
            startIcon={
              sentAction === "stop filt-man" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflDfa3500.buttons.stopFiltrazione")}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStartDisoMan}
            onClick={() => setAction("start diso-man")}
            startIcon={
              sentAction === "start diso-man" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflDfa3500.buttons.startDisoleatore")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStopDisoMan}
            onClick={() => setAction("stop diso-man")}
            startIcon={
              sentAction === "stop diso-man" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflDfa3500.buttons.stopDisoleatore")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
